"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  label: "§ 05.1 — GLOBAL SOURCING",
  value: 450,
  suffix: "",
  unit: "PARTNERS · DIRECT MANUFACTURING",
  note: "Strategic partnerships across China and Southeast Asia.",
  color: "sky"
}, {
  label: "§ 05.2 — UAE LOGISTICS",
  value: 12000,
  suffix: "",
  unit: "SQM · WAREHOUSING CAPACITY",
  note: "Advanced storage facilities in Sharjah, Ajman, and Al Ain.",
  color: "aluminum"
}, {
  label: "§ 05.3 — PRODUCT RANGE",
  value: 850,
  suffix: "",
  unit: "SKUS · ARCHITECTURAL HARDWARE",
  note: "Precision-engineered profiles for commercial contractors.",
  color: "sky"
}, {
  label: "§ 05.4 — CLIENT REACH",
  value: 1200,
  suffix: "",
  unit: "PROJECTS · COMPLETED ACROSS UAE",
  note: "Trusted by leading interior design and construction firms.",
  color: "aluminum"
}];
const tickerItems = ["SHARJAH", "AJMAN", "AL AIN", "GUANGZHOU", "ALUMINUM-PROFILES", "ARCHITECTURAL-HARDWARE", "B2B-SUPPLY", "LOGISTICS-FLOW", "SHARJAH", "AJMAN", "AL AIN", "GUANGZHOU"];
function useCountUp(target: number, start: boolean, duration = 1200) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!start || startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const ease = (t: number) => {
      return 1 - Math.pow(1 - t, 3);
    };
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      setValue(Math.round(target * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}
function StatBlock({
  stat,
  index,
  triggered
}: {
  stat: (typeof stats)[number];
  index: number;
  triggered: boolean;
}) {
  const animated = useCountUp(stat.value, triggered);
  const colorStyle = stat.color === "sky" ? {
    color: "hsl(199 89% 48%)"
  } : {
    color: "hsl(215 16% 47%)"
  };
  return <div data-index={index} className="relative flex flex-col gap-4 px-0 py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0">
      <div className="font-default uppercase" style={{
      fontSize: "0.875rem",
      letterSpacing: "0.2em",
      fontWeight: 500,
      color: "hsl(215 16% 47%)"
    }}>
        <span style={{
        color: "hsl(199 89% 48%)"
      }}>
          {stat.label.split("—")[0].trim()}
        </span>
        <span style={{
        color: "hsl(215 16% 47%)"
      }}>
          {" "}— {stat.label.split("—")[1].trim()}
        </span>
      </div>

      <div className="font-mono" style={{
      fontSize: "clamp(3.5rem, 7.5vw, 7.5rem)",
      fontWeight: 800,
      letterSpacing: "-0.04em",
      lineHeight: 0.95,
      ...colorStyle
    }}>
        {animated.toLocaleString()}
      </div>

      <div className="font-default uppercase" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.05em",
      fontWeight: 400,
      color: "hsl(210 40% 98%)"
    }}>
        {stat.unit}
      </div>

      <p className="font-default" style={{
      fontSize: "0.9375rem",
      lineHeight: 1.6,
      color: "hsl(215 16% 70%)",
      maxWidth: "28ch"
    }}>
        {stat.note}
      </p>
    </div>;
}
export default function MarketImpact() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  return <section data-section-id="2115" id="our-market-impact" className="dark relative overflow-hidden bg-background text-foreground" style={{
    backgroundColor: "hsl(222 47% 7%)",
    color: "hsl(210 40% 98%)"
  }}>
      <div className="relative w-full overflow-hidden border-b" style={{
      borderColor: "hsl(217 33% 25%)",
      backgroundColor: "hsl(222 47% 7%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-mono inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
          fontWeight: 400,
          color: "hsl(215 16% 70%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(199 89% 48%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>

      <div ref={sectionRef} className="relative mx-auto w-full max-w-[1440px] px-5 md:px-8 lg:px-14 py-28 md:py-36">
        <div aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 select-none font-default hidden md:block" style={{
        fontSize: "860px",
        lineHeight: 1,
        fontWeight: 800,
        WebkitTextStroke: "1px hsl(210 40% 98%)",
        color: "transparent",
        opacity: 0.03
      }}>
          05
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-7 flex flex-col gap-7">
            <div className="font-default uppercase" style={{
            fontSize: "0.875rem",
            letterSpacing: "0.2em",
            fontWeight: 500
          }}>
              <span style={{
              color: "hsl(199 89% 48%)"
            }}>§ 05</span>
              <span style={{
              color: "hsl(215 16% 47%)"
            }}>
                {" "}— GLOBAL SUPPLY CHAIN EXPERTISE
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1" style={{
              backgroundColor: "hsl(217 33% 25%)"
            }} />
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(199 89% 48%)"
              }} />
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(199 89% 48%)"
              }} />
                <span className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "hsl(199 89% 48%)"
              }} />
              </div>
            </div>

            <h2 className="font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            color: "hsl(210 40% 98%)"
          }}>
              Global Supply Chain Expertise
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end gap-6">
            <p className="font-default" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6,
            color: "hsl(215 16% 70%)"
          }}>
              World Sky Building Materials Trading LLC bridges global manufacturing 
              excellence with local logistical mastery, ensuring reliable supply 
              chains for contractors across the Emirates.
            </p>

            <Link to="/ConnectWithSalesPage" className="group inline-flex items-center gap-2 self-start font-default" style={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "hsl(199 89% 48%)"
          }}>
              <span className="relative inline-block">
                Inquire with sales
                <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-x-100" style={{
                backgroundColor: "hsl(199 89% 48%)"
              }} aria-hidden="true" />
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="mt-20 mb-12 flex items-center gap-3">
          <div className="h-px flex-1" style={{
          backgroundColor: "hsl(217 33% 25%)"
        }} />
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(199 89% 48%)"
          }} />
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(199 89% 48%)"
          }} />
            <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(199 89% 48%)"
          }} />
          </div>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={`relative ${i > 0 ? "lg:border-l" : ""} ${i % 2 === 1 ? "sm:border-l lg:border-l" : ""} ${i >= 2 ? "sm:border-t lg:border-t-0" : ""} border-t sm:border-t-0 first:border-t-0`} style={{
          borderColor: "hsl(217 33% 25%)"
        }}>
              <StatBlock stat={stat} index={i} triggered={isVisible} />
            </div>)}
        </div>
      </div>

      <div className="relative w-full overflow-hidden border-t" style={{
      borderColor: "hsl(217 33% 25%)",
      backgroundColor: "hsl(222 47% 7%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-mono inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
          fontWeight: 400,
          color: "hsl(215 16% 70%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1 rounded-lg" style={{
            backgroundColor: "hsl(199 89% 48%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>
    </section>;
}